import React, { useEffect, useState, useContext } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Popover from "@material-ui/core/Popover";
import WarningIcon from "@material-ui/icons/Warning";
import SettingsIcon from "@material-ui/icons/Settings";
import CloseIcon from "@material-ui/icons/Close";
import AddIcon from "@material-ui/icons/Add";
import {
  Badge,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Switch,
  Divider,
  Box,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import ServiceTypeSlaModal from "../ServiceTypeSlaModal";
import ConfirmationModal from "../ConfirmationModal";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    maxHeight: 380,
    minWidth: 340,
    maxWidth: 420,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "6px 10px",
  },
}));

export default function SlaAlertsPopover() {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);

  const [anchorEl, setAnchorEl] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [slaResponseMinutes, setSlaResponseMinutes] = useState(15);
  const [queues, setQueues] = useState([]);
  const [serviceTypeSlas, setServiceTypeSlas] = useState([]);
  const [slaModalOpen, setSlaModalOpen] = useState(false);
  const [editingSlaId, setEditingSlaId] = useState(null);
  const [deletingSla, setDeletingSla] = useState(null);

  const isSupervisor = user?.profile === "admin" || user?.profile === "supervisor";

  const fetchAlerts = async () => {
    try {
      const { data } = await api.get("/sla-alerts");
      setAlerts(data.alerts || []);
    } catch (err) {
      // silencioso: não bloqueia o restante do app
    }
  };

  useEffect(() => {
    if (isSupervisor) fetchAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isSupervisor || !user?.companyId) return undefined;
    const companyId = user.companyId;

    const onSlaAlert = (data) => {
      if (data.action === "create") {
        setAlerts((prev) => [data.alert, ...prev.filter((a) => a.id !== data.alert.id)]);
      }
      if (data.action === "resolve") {
        setAlerts((prev) => prev.filter((a) => a.id !== data.alert.id));
      }
    };
    socket.on(`company-${companyId}-slaAlert`, onSlaAlert);
    return () => {
      socket.off(`company-${companyId}-slaAlert`, onSlaAlert);
    };
  }, [isSupervisor, user, socket]);

  const fetchSettings = async () => {
    try {
      const { data } = await api.get("/sla-settings");
      setSlaResponseMinutes(data.slaResponseMinutes);
    } catch (err) {
      toastError(err);
    }
  };

  const fetchQueuesAndSlas = async () => {
    try {
      const [{ data: queuesData }, { data: slasData }] = await Promise.all([
        api.get("/queue"),
        api.get("/service-type-sla"),
      ]);
      setQueues(Array.isArray(queuesData) ? queuesData : queuesData?.queues || []);
      setServiceTypeSlas(Array.isArray(slasData) ? slasData : slasData?.data || []);
    } catch (err) {
      toastError(err);
    }
  };

  const openSettings = () => {
    setSettingsOpen(true);
    fetchSettings();
    fetchQueuesAndSlas();
  };

  const saveResponseMinutes = async () => {
    try {
      await api.put("/sla-settings", { slaResponseMinutes });
    } catch (err) {
      toastError(err);
    }
  };

  const toggleServiceTypeSla = async (sla) => {
    try {
      await api.put(`/service-type-sla/${sla.id}`, { ativo: !sla.ativo });
      fetchQueuesAndSlas();
    } catch (err) {
      toastError(err);
    }
  };

  const openNewSlaModal = () => {
    setEditingSlaId(null);
    setSlaModalOpen(true);
  };

  const openEditSlaModal = (sla) => {
    setEditingSlaId(sla.id);
    setSlaModalOpen(true);
  };

  const confirmDeleteSla = async () => {
    if (!deletingSla) return;
    try {
      await api.delete(`/service-type-sla/${deletingSla.id}`);
      fetchQueuesAndSlas();
    } catch (err) {
      toastError(err);
    } finally {
      setDeletingSla(null);
    }
  };

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const goToTicket = (alert) => {
    const uuid = alert.ticket?.uuid;
    if (uuid) history.push(`/tickets/${uuid}`);
    handleClose();
  };

  if (!isSupervisor) return null;

  const open = Boolean(anchorEl);

  return (
    <div>
      <IconButton aria-describedby="sla-alerts-popover" onClick={handleClick} style={{ color: theme.palette.primary.main }}>
        <Badge color="error" badgeContent={alerts.length} max={99}>
          <WarningIcon />
        </Badge>
      </IconButton>
      <Popover
        id="sla-alerts-popover"
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Paper variant="outlined" className={classes.mainPaper}>
          <div className={classes.header}>
            <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
              Alertas de SLA
            </Typography>
            <IconButton size="small" onClick={openSettings} title="Configurar limites">
              <SettingsIcon fontSize="small" />
            </IconButton>
          </div>
          <Divider />
          <List component="nav" style={{ minWidth: 300 }}>
            {alerts.length === 0 && (
              <ListItemText
                primary="Nenhum alerta ativo no momento."
                style={{ padding: "12px 16px" }}
              />
            )}
            {alerts.map((alert) => (
              <ListItem
                key={alert.id}
                button
                onClick={() => goToTicket(alert)}
                style={{ borderLeft: "4px solid #e7505a", marginBottom: 4 }}
              >
                <ListItemText
                  primary={alert.ticket?.contact?.name || "Contato"}
                  secondary={
                    <>
                      <Chip
                        size="small"
                        label={alert.type === "espera_fila" ? "Esperando na fila" : "Sem resposta"}
                        style={{ marginRight: 6 }}
                      />
                      {alert.minutosDecorridos} min
                      {alert.queue?.name ? ` · ${alert.queue.name}` : ""}
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      </Popover>

      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Configurar Alertas de SLA
          <IconButton
            onClick={() => setSettingsOpen(false)}
            style={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" gutterBottom>
            Tempo máximo sem resposta do atendente (minutos)
          </Typography>
          <TextField
            type="number"
            size="small"
            value={slaResponseMinutes}
            onChange={(e) => setSlaResponseMinutes(e.target.value)}
            style={{ width: 120, marginBottom: 16 }}
          />

          <Box style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8, marginBottom: 4 }}>
            <Typography variant="subtitle2">
              Tempo máximo de espera na fila, por fila
            </Typography>
            <Button
              size="small"
              color="primary"
              startIcon={<AddIcon fontSize="small" />}
              onClick={openNewSlaModal}
            >
              Nova regra
            </Button>
          </Box>

          {serviceTypeSlas.length === 0 && (
            <Typography variant="body2" color="textSecondary" style={{ padding: "12px 4px" }}>
              Nenhuma regra de SLA cadastrada ainda.
            </Typography>
          )}

          {serviceTypeSlas.map((sla) => (
            <Paper
              key={sla.id}
              variant="outlined"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 12px",
                marginBottom: 8,
                opacity: sla.ativo ? 1 : 0.55,
              }}
            >
              <div>
                <Typography variant="body2" style={{ fontWeight: "bold" }}>
                  {sla.queue?.name || `Fila #${sla.queueId}`}
                  <Chip
                    size="small"
                    label={`${sla.tempoLimiteMinutos} min`}
                    style={{ marginLeft: 8 }}
                  />
                </Typography>
                {sla.descricao && (
                  <Typography variant="caption" color="textSecondary">
                    {sla.descricao}
                  </Typography>
                )}
              </div>
              <Box style={{ display: "flex", alignItems: "center" }}>
                <Switch
                  size="small"
                  checked={!!sla.ativo}
                  onChange={() => toggleServiceTypeSla(sla)}
                  title={sla.ativo ? "Ativa" : "Inativa"}
                />
                <IconButton size="small" onClick={() => openEditSlaModal(sla)} title="Editar">
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => setDeletingSla(sla)} title="Excluir">
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            </Paper>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>Fechar</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={async () => {
              await saveResponseMinutes();
              setSettingsOpen(false);
            }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <ServiceTypeSlaModal
        open={slaModalOpen}
        onClose={() => setSlaModalOpen(false)}
        slaId={editingSlaId}
        queues={queues}
        onSaved={fetchQueuesAndSlas}
      />

      <ConfirmationModal
        title="Excluir regra de SLA?"
        open={Boolean(deletingSla)}
        onClose={() => setDeletingSla(null)}
        onConfirm={confirmDeleteSla}
      >
        Essa regra de SLA para a fila "{deletingSla?.queue?.name || deletingSla?.queueId}" será removida e não vai mais gerar alertas.
      </ConfirmationModal>
    </div>
  );
}
